import { notifications } from '@mantine/notifications';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { useCallback } from 'react';

import { ErrorIcon, Success } from '@apple/assets/icons';
import { useTranslation } from '@apple/lib/i18next';
import { useBackOrNavigate } from '@apple/lib/tanstack-router/hooks/use-router-history';
import { ServerValidationError } from '@apple/utils/api/errors';

import { requireAuth } from '@apple/features/auth/utils/require-auth';
import { abandonMessage, requeueMessage } from '@apple/features/queue-message/api';
import { QueueMessage } from '@apple/features/queue-message/components/QueueMessage';
import { queueMessageIdSchema } from '@apple/features/queue-message/models';
import { queueMessageQueries, queueMessageQueryKeys } from '@apple/features/queue-message/queries';

export const Route = createFileRoute('/_authed/_admin/manage/queue-message_/$messageId')({
	params: {
		parse: ({ messageId }) => ({
			messageId: queueMessageIdSchema.parse(messageId),
		}),
	},

	beforeLoad: args => {
		requireAuth(args, {
			requirePermission: 'Manzanita.Security.Features.QueueMessageManagement.Read',
		});
	},

	loader: async ({ context: { queryClient }, params: { messageId } }) => {
		return await queryClient.ensureQueryData(queueMessageQueries.getQueueMessage(messageId));
	},

	wrapInSuspense: true,
	component: QueueMessageDetailsRoute,
});

function QueueMessageDetailsRoute() {
	const queueMessage = Route.useLoaderData();

	const { t } = useTranslation('manage');
	const queryClient = useQueryClient();
	const goBackToList = useBackOrNavigate();

	const onSuccessfulChange = useCallback(
		async (message: string) => {
			await queryClient.invalidateQueries({
				queryKey: queueMessageQueryKeys.all,
				refetchType: 'all',
			});

			notifications.show({
				message,
				icon: <Success />,
				color: 'green',
				autoClose: 5000,
			});

			await goBackToList('/manage/queue-message');
		},
		[goBackToList, queryClient],
	);

	const onFailedChange = useCallback(
		(error: Error) => {
			if (!(error instanceof ServerValidationError)) {
				notifications.show({
					message: t('common:error.generic'),
					icon: <ErrorIcon />,
					color: 'red',
					autoClose: 5000,
				});
			}
		},
		[t],
	);

	const requeueMutation = useMutation({
		mutationFn: requeueMessage,
		onSuccess: async () => {
			await onSuccessfulChange(t('queues.notifications.requeued'));
		},
		onError: onFailedChange,
	});

	const abandonMutation = useMutation({
		mutationFn: abandonMessage,
		onSuccess: async () => {
			await onSuccessfulChange(t('queues.notifications.abandoned'));
		},
		onError: onFailedChange,
	});

	return (
		<QueueMessage
			message={queueMessage}
			onRequeue={async id => {
				await requeueMutation.mutateAsync(queueMessageIdSchema.parse(id));
			}}
			onAbandon={async id => {
				await abandonMutation.mutateAsync(queueMessageIdSchema.parse(id));
			}}
		/>
	);
}
